import React, { isValidElement, useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { keyframes } from "tss-react";
import { makeStyles } from "tss-react/mui";
import Icon from "../../../base/icons/components/Icon";
import { IconCheck, IconInfo, IconMessage, IconUser, IconUsers, IconWarningCircle, } from "../../../base/icons/svg";
import Message from "../../../base/react/components/web/Message";
import { getSupportUrl } from "../../../base/react/functions";
import { NOTIFICATION_ICON, NOTIFICATION_TYPE } from "../../constants";
import { NotificationsTransitionContext } from "../NotificationsTransition";
/**
 * Secondary colors for notification icons.
 *
 * @type {{error, info, normal, success, warning}}
 */
const useStyles = makeStyles()((theme) => {
    return {
        container: {
            backgroundColor: theme.palette.ui10,
            padding: "6px 12px",
            display: "flex",
            position: "relative",
            borderRadius: "20px",
            boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.2)",
            marginBottom: theme.spacing(1),
            "&:last-of-type": {
                marginBottom: 0,
            },
            animation: `${keyframes `
                0% {
                    opacity: 0;
                    transform: translateY(20px);
                }
                100% {
                    opacity: 1;
                    transform: translateY(0);
                }
            `} 0.5s forwards ease`,
            "&.unmount": {
                animation: `${keyframes `
                    0% {
                        opacity: 1;
                        transform: translateY(0);
                    }
                    100% {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                `} 0.5s forwards ease`,
            },
        },
        ribbon: {
            display: "none",
        },
        content: {
            display: "flex",
            alignItems: "center",
            padding: "4px 0",
            flex: 1,
            maxWidth: "100%",
        },
        textContainer: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "8px",
            color: theme.palette.text04,
            flex: 1,
            margin: "0 6px",
            maxWidth: "calc(100% - 36px - 12px)",
            "@media (max-width: 580px)": {
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "4px",
            },
        },
        title: {
            ...theme.typography.bodyShortBold,
            fontSize: "0.8rem",
            whiteSpace: "nowrap",
        },
        description: {
            display: "none",
        },
        actionsContainer: {
            display: "flex",
            flexShrink: 0,
        },
        action: {
            border: 0,
            outline: 0,
            backgroundColor: "transparent",
            color: theme.palette.action01,
            ...theme.typography.bodyShortBold,
            fontSize: "0.75rem",
            marginRight: theme.spacing(2),
            padding: 0,
            cursor: "pointer",
            "&:last-of-type": {
                marginRight: 0,
            },
            "&.destructive": {
                color: theme.palette.textError,
            },
            "&:focus-visible": {
                outline: `2px solid ${theme.palette.action01}`,
                outlineOffset: 2,
            },
        },
        closeIcon: {
            cursor: "pointer",
        },
    };
});
const Notification = ({ appearance = NOTIFICATION_TYPE.NORMAL, customActionHandler, customActionNameKey, customActionType, description, descriptionArguments, descriptionKey, disableClosing, hideErrorSupportLink, icon, onDismissed, title, titleArguments, titleKey, uid, }) => {
    const { classes, cx, theme } = useStyles();
    const { t } = useTranslation();
    const { unmounting } = useContext(NotificationsTransitionContext);
    const supportUrl = useSelector(getSupportUrl);
    const isErrorOrWarning = useMemo(() => appearance === NOTIFICATION_TYPE.ERROR || appearance === NOTIFICATION_TYPE.WARNING, [appearance]);
    const ICON_COLOR = {
        error: theme.palette.iconError,
        normal: theme.palette.action01,
        success: theme.palette.success01,
        warning: theme.palette.warning01,
    };
    const onDismiss = useCallback(() => {
        onDismissed(uid);
    }, [uid]);
    // eslint-disable-next-line react/no-multi-comp
    const renderDescription = useCallback(() => {
        const descriptionArray = [];
        descriptionKey && descriptionArray.push(t(descriptionKey, descriptionArguments));
        description && typeof description === "string" && descriptionArray.push(description);
        // Keeping in mind that:
        // - Notifications that use the `translateToHtml` function get an element-based description array with one entry
        // - Message notifications receive string-based description arrays that might need additional parsing
        // We look for ready-to-render elements, and if present, we roll with them
        // Otherwise, we use the Message component that accepts a string `text` prop
        const shouldRenderHtml = descriptionArray.length === 1 && isValidElement(descriptionArray[0]);
        // the id is used for testing the UI
        return (React.createElement("div", { className: classes.description, "data-testid": descriptionKey },
            shouldRenderHtml ? descriptionArray : React.createElement(Message, { text: descriptionArray.join(" ") }),
            typeof description === "object" && description));
    }, [description, descriptionArguments, descriptionKey, classes]);
    const _onOpenSupportLink = useCallback(() => {
        window.open(supportUrl, "_blank", "noopener");
    }, [supportUrl]);
    const processCustomActions = (key, handler, type) => {
        if (key?.length && handler?.length) {
            return key.map((customAction, customActionIndex) => {
                return {
                    content: t(customAction),
                    onClick: () => {
                        if (handler?.[customActionIndex]()) {
                            onDismiss();
                        }
                    },
                    type: type?.[customActionIndex],
                    testId: customAction,
                };
            });
        }
        return [];
    };
    const mapAppearanceToButtons = useCallback(() => {
        switch (appearance) {
            case NOTIFICATION_TYPE.ERROR: {
                const buttons = [
                    {
                        content: t("dialog.dismiss"),
                        onClick: onDismiss,
                    },
                ];
                if (!hideErrorSupportLink && supportUrl) {
                    buttons.push({
                        content: t("dialog.contactSupport"),
                        onClick: _onOpenSupportLink,
                    });
                }
                return processCustomActions(customActionNameKey, customActionHandler, customActionType).concat(buttons);
            }
            case NOTIFICATION_TYPE.WARNING:
                return [
                    {
                        content: t("dialog.Ok"),
                        onClick: onDismiss,
                    },
                ];
            default:
                return processCustomActions(customActionNameKey, customActionHandler, customActionType);
        }
    }, [appearance, onDismiss, customActionHandler, customActionNameKey, hideErrorSupportLink, supportUrl]);
    const getIcon = useCallback(() => {
        let iconToDisplay;
        switch (icon || appearance) {
            case NOTIFICATION_ICON.ERROR:
            case NOTIFICATION_ICON.WARNING:
                iconToDisplay = IconWarningCircle;
                break;
            case NOTIFICATION_ICON.SUCCESS:
                iconToDisplay = IconCheck;
                break;
            case NOTIFICATION_ICON.MESSAGE:
                iconToDisplay = IconMessage;
                break;
            case NOTIFICATION_ICON.PARTICIPANT:
                iconToDisplay = IconUser;
                break;
            case NOTIFICATION_ICON.PARTICIPANTS:
                iconToDisplay = IconUsers;
                break;
            default:
                iconToDisplay = IconInfo;
                break;
        }
        return iconToDisplay;
    }, [icon, appearance]);
    return (React.createElement("div", { "aria-atomic": true, "aria-live": isErrorOrWarning ? "assertive" : "polite", className: cx(classes.container, (unmounting.get(uid ?? "") && "unmount")), "data-testid": titleKey || descriptionKey, id: uid, role: isErrorOrWarning ? "alert" : "status" },
        React.createElement("div", { className: classes.content },
            React.createElement("div", { className: icon },
                React.createElement(Icon, { color: ICON_COLOR[appearance], size: 16, src: getIcon() })),
            React.createElement("div", { className: classes.textContainer },
                React.createElement("span", { className: classes.title }, title || t(titleKey ?? "", titleArguments)),
                renderDescription(),
                React.createElement("div", { className: classes.actionsContainer }, mapAppearanceToButtons().map(({ content, onClick, type, testId }) => (React.createElement("button", { "aria-label": content, className: cx(classes.action, type), "data-testid": testId, key: content, onClick: onClick, type: "button" }, content))))))));
};
export default Notification;
